import Component from '@glimmer/component';
import { tracked, cached } from '@glimmer/tracking';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import Button from 'tio-ui/components/button';
import Modal from 'tio-ui/components/modal';
import RSelect from 'tio-ui/components/select';
import Form from 'tio-ui/components/form';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import { VStack } from 'tio-ui/components/layout';
import { and, or } from 'tio-ui/utilities';
import { trackedFunction } from 'reactiveweb/function';
import PartnerVisibilityConfig from './partner-visibility-config.gts';
import * as v from 'valibot';
import { createReportRequest, updateReportRequest } from '../requests/report';
import { SkipCache } from '@warp-drive/core-types/request';
import { query } from '@warp-drive/utilities/json-api';
import type { FormErrors, FormResult } from 'tio-ui/components/form';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type Owner from '@ember/owner';

/**
 * The typed form data structure for the save report dialog.
 * This interface defines the exact shape of data passed through FormResult<SaveReportFormData>
 */
interface SaveReportFormData {
  [key: string]: unknown;
  name: string;
  description: string;
  visibilityLevel: string;
}

interface SaveReportDialogSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    onSave: (report: Report) => void;

    // The raw data from the search builder (flexible JSON)
    searchData: Record<string, unknown>;

    // Optional serialized query parameters for URL rehydration
    serializedQuery?: string;

    reportType: 'chart_builder' | 'table_builder' | 'dashboard';

    // Current report being viewed (if any)
    currentReport?: Report;

    // Whether to show partner visibility options (only for global/cross-company context)
    showPartnerVisibility?: boolean;

    // Company-scope visibility gating, mirroring report-builder/publish-modal:
    // when provided, a chosen companyId offers Private / Custom Partner (with
    // partner access locked to that company) and no companyId offers
    // Private / Global. Omit to keep the legacy showPartnerVisibility options.
    companyScope?: { companyId?: string; companyName?: string };

    // Extra metadata merged into the saved report (e.g. dashboard products/datasets)
    extraMetadata?: Record<string, unknown>;

    // Pre-fill the name for a brand-new report (e.g. the dashboard's working title)
    initialName?: string;
  };
}

export default class SaveReportDialog extends Component<SaveReportDialogSignature> {
  @service declare store: StoreService;

  @tracked saveMode: 'update' | 'create_new' = 'update';
  @tracked partnerAccessConfig: number[] = [];
  @tracked currentVisibilityLevel = 'private';

  constructor(owner: Owner, args: SaveReportDialogSignature['Args']) {
    super(owner, args);

    // Initialize currentVisibilityLevel from existing report if available
    if (args.currentReport?.visibilityLevel) {
      this.currentVisibilityLevel = args.currentReport.visibilityLevel;

      // Initialize partner access config if it exists
      if (args.currentReport.partnerAccessConfig) {
        this.partnerAccessConfig = [...args.currentReport.partnerAccessConfig];
      }
    }
  }

  static MAX_DESCRIPTION_LENGTH = 120;

  get maxDescriptionLength() {
    return SaveReportDialog.MAX_DESCRIPTION_LENGTH;
  }

  FormSchema = v.object({
    name: v.pipe(v.string(), v.trim(), v.minLength(1, 'Name is required')),
    description: v.pipe(v.string(), v.trim()),
    visibilityLevel: v.picklist(['private', 'partner', 'global']),
  });

  descriptionLength = (value: unknown) => {
    return typeof value === 'string' ? value.length : 0;
  };

  isDescriptionAtLimit = (value: unknown) => {
    return typeof value === 'string' && value.length >= SaveReportDialog.MAX_DESCRIPTION_LENGTH;
  };

  availableCompanies = trackedFunction(this, async () => {
    const { content } = (await this.store.request(
      query<CompanyModel>('company', {
        'filter[is-active]': 1,
        sort: 'name',
        'page[limit]': 400,
      })
    )) as { content: { data: CompanyModel[] } };
    return content.data;
  });

  /**
   * Under company-scope gating, partner access is locked to the builder's
   * selected company (mirrors publish-modal): the partner picker is hidden and
   * saves always carry exactly that company.
   */
  get scopeLockedPartnerAccess() {
    const companyId = this.args.companyScope?.companyId;
    return companyId ? [parseInt(companyId, 10)] : undefined;
  }

  /**
   * Clamp a visibility level to the options offered under company-scope
   * gating: a stored value the gate no longer offers (e.g. a Global dashboard
   * whose builder scope later chose a company, or the inverse) falls back to
   * 'private' instead of silently submitting an invalid scope/visibility
   * combination. Legacy (non-gated) callers keep whatever the report carries.
   */
  #clampVisibility(level: string) {
    if (!this.args.companyScope) return level;
    return this.visibilityOptions.some((option) => option.key === level) ? level : 'private';
  }

  /** The select's effective value — the tracked level, gated by companyScope. */
  get selectedVisibilityLevel() {
    return this.#clampVisibility(this.currentVisibilityLevel);
  }

  get visibilityOptions() {
    const scope = this.args.companyScope;
    if (scope) {
      // Mirror report-builder/publish-modal: company-scoped data can only be
      // private or shared with that partner; cross-company data can go global.
      if (scope.companyId) {
        return [
          { key: 'private', label: 'Private (Only me)' },
          { key: 'partner', label: 'Custom Partner Report' },
        ];
      }
      return [
        { key: 'private', label: 'Private (Only me)' },
        { key: 'global', label: 'Global (Everyone)' },
      ];
    }

    const options = [
      { key: 'private', label: 'Private (Only me)' },
      { key: 'global', label: 'Global (Everyone)' },
    ];

    if (this.args.showPartnerVisibility) {
      options.splice(1, 0, { key: 'partner', label: 'Custom Partner Report' });
    }

    return options;
  }

  get saveModeOptions() {
    return [
      { key: 'update', label: `Update "${this.args.currentReport?.displayName}"` },
      { key: 'create_new', label: 'Save as new report' },
    ];
  }

  get hasCurrentReport() {
    return Boolean(this.args.currentReport);
  }

  @cached
  get defaultFormData() {
    if (this.hasCurrentReport && this.saveMode === 'update') {
      const currentReport = this.args.currentReport!;
      return {
        name: currentReport.name || '',
        description: currentReport.description || '',
        visibilityLevel: this.#clampVisibility(currentReport.visibilityLevel || 'private'),
      };
    }

    // Default form data for new reports or when copying an existing report
    return {
      name: this.hasCurrentReport
        ? `${this.args.currentReport!.name} (Copy)`
        : (this.args.initialName ?? ''),
      description: this.hasCurrentReport ? this.args.currentReport!.description || '' : '',
      visibilityLevel: 'private',
    };
  }

  validate = (data: SaveReportFormData): FormErrors => {
    const parseResult = v.safeParse(this.FormSchema, data);

    if (parseResult.success) {
      return {};
    }

    const validationErrors: Record<string, string[]> = {};

    for (const issue of parseResult.issues) {
      const path = issue.path?.[0]?.key as string;
      if (path) {
        if (!validationErrors[path]) {
          validationErrors[path] = [];
        }
        validationErrors[path].push(issue.message);
      }
    }

    return validationErrors;
  };

  onSubmit = dropTask(async (result: FormResult<SaveReportFormData>) => {
    // No casting needed - FormResult is properly typed with SaveReportFormData
    const { data } = result;
    // Re-clamp at submit so a stale form value can't slip through — covers a
    // builder scope change while this (persistently mounted) dialog was open.
    const visibilityLevel = this.#clampVisibility(data.visibilityLevel);

    try {
      // Extract serializedQuery from searchData if not provided separately
      const serializedQuery =
        this.args.serializedQuery || (this.args.searchData?.urlParams as string) || '';

      // Create properly structured search config based on report type
      const searchConfig = this.createSearchConfig(this.args.searchData, this.args.reportType);

      let savedReport: Report;

      if (this.hasCurrentReport && this.saveMode === 'update') {
        // Update existing report — preserve existing scope/publish state in metadata
        const currentReport = this.args.currentReport!;
        if (!currentReport.id) return;

        // Spread extraMetadata first so the reserved keys below always win.
        const metadata = {
          ...(currentReport.metadata || {}),
          ...(this.args.extraMetadata ?? {}),
          saved_at: new Date().toISOString(),
          serializedQuery: serializedQuery,
        };

        const request = updateReportRequest(currentReport.id, {
          name: data.name,
          description: data.description || '',
          visibilityLevel,
          partnerAccessConfig: this.scopeLockedPartnerAccess ?? this.partnerAccessConfig,
          searchConfig: searchConfig,
          metadata: metadata,
        });
        request.cacheOptions = { [SkipCache]: true };
        const response = await this.store.request(request);
        // Merge updated fields back onto the currentReport object so parent stays in sync
        const attrs = (response.content as { data: { attributes: Record<string, unknown> } })?.data
          ?.attributes;
        if (attrs) {
          Object.assign(currentReport, attrs);
        }
        savedReport = currentReport;
      } else {
        // Create new report — defaults to unpublished (admin_only scope)
        // Spread extraMetadata first so the reserved keys below always win.
        const metadata = {
          ...(this.args.extraMetadata ?? {}),
          saved_at: new Date().toISOString(),
          serializedQuery: serializedQuery,
          scope: 'admin_only' as const,
        };

        const request = createReportRequest({
          name: data.name,
          description: data.description || '',
          reportType: this.args.reportType,
          visibilityLevel,
          partnerAccessConfig: this.scopeLockedPartnerAccess ?? this.partnerAccessConfig,
          searchConfig: searchConfig,
          metadata: metadata,
        });
        request.cacheOptions = { [SkipCache]: true };
        const response = await this.store.request(request);
        const responseData = response.content as {
          data: { id: string; attributes: Record<string, unknown> };
        };
        // Build a plain Report-like object from the response
        savedReport = {
          id: responseData.data.id,
          ...responseData.data.attributes,
        } as unknown as Report;
      }

      // Notify parent
      this.args.onSave(savedReport);
      this.args.onClose();
    } catch (error) {
      // Handle error - could set a general error message
      console.error('Failed to save report:', error);
    }
  });

  updateSaveMode = (key: string | null) => {
    if (key) {
      this.saveMode = key as 'update' | 'create_new';
    }
  };

  handleVisibilitySelect = (setFieldValue: (value: string) => void, key: string | null) => {
    if (key) {
      setFieldValue(key);
      this.currentVisibilityLevel = key;
    }
  };

  handleCancel = () => {
    this.args.onClose();
  };

  handlePartnerAccessChange = (config: number[]) => {
    this.partnerAccessConfig = config;
  };

  // No-op since visibility is handled by the main form
  handleVisibilityLevelChange = () => {};

  handleFormChange = (result: FormResult<SaveReportFormData>) => {
    // No casting needed - FormResult is properly typed
    const { data } = result;

    if (data.visibilityLevel) {
      this.currentVisibilityLevel = data.visibilityLevel;
    }
  };

  /**
   * Create properly structured search config based on report type
   * Separates chart settings from actual query filters
   */
  createSearchConfig = (
    searchData: Record<string, unknown>,
    _reportType: 'chart_builder' | 'table_builder' | 'dashboard'
  ): Record<string, unknown> => {
    // Use the searchData as-is since it's already properly structured
    // from reportSearchConfig in the parent component
    return searchData;
  };

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{this.handleCancel}}
      @size="lg"
      data-test-save-report-dialog
    >
      <:header>
        Save
        {{if
          (eq @reportType "dashboard")
          "Dashboard"
          (if (eq @reportType "chart_builder") "Chart" "Table")
        }}
        {{unless (eq @reportType "dashboard") "Report"}}
      </:header>

      <:body>
        <Form
          @data={{this.defaultFormData}}
          @validate={{this.validate}}
          @onChange={{this.handleFormChange}}
          @onSubmit={{this.onSubmit.perform}}
          id="save-report-form"
          as |form|
        >
          <VStack class="tio-col-loose">
            {{#if this.hasCurrentReport}}
              <RSelect
                @label="Save Option"
                @items={{this.saveModeOptions}}
                @selected={{this.saveMode}}
                @onChange={{this.updateSaveMode}}
              />
            {{/if}}

            {{#let (form.field "name") as |field|}}
              <Input
                @name="name"
                @label="Report Name"
                @value={{field.value}}
                @onChange={{field.onChange}}
                @onBlur={{field.onBlur}}
                @isRequired={{true}}
                @errors={{field.errors}}
              />
            {{/let}}

            {{#let (form.field "description") as |field|}}
              <Textarea
                @name="description"
                @label="Description (Optional)"
                @value={{field.value}}
                @onChange={{field.onChange}}
                @onBlur={{field.onBlur}}
                @errors={{field.errors}}
                @maxlength={{this.maxDescriptionLength}}
              />
              <p
                class="-mt-1 text-xs
                  {{if
                    (this.isDescriptionAtLimit field.value)
                    'tio-fg-danger tio-text-xs-medium'
                    'tio-fg-muted'
                  }}"
              >
                {{this.descriptionLength field.value}}/{{this.maxDescriptionLength}}
                — Shown on report cards
              </p>
            {{/let}}

            {{#let (form.field "visibilityLevel") as |field|}}
              <RSelect
                @label="Visibility"
                @items={{this.visibilityOptions}}
                @selected={{this.selectedVisibilityLevel}}
                @onChange={{fn this.handleVisibilitySelect field.onChange}}
              />
            {{/let}}

            {{#if (eq this.selectedVisibilityLevel "partner")}}
              {{#if this.scopeLockedPartnerAccess}}
                {{! Partner access is locked to the builder's company scope —
                    no picker, mirroring the report builder's publish modal. }}
                <p class="text-sm tio-fg-muted" data-test-save-report-partner-scope-note>
                  Visible to
                  <strong class="tio-fg-primary">{{if
                      @companyScope.companyName
                      @companyScope.companyName
                      "the selected company"
                    }}</strong>
                  when published.
                </p>
              {{else}}
                <PartnerVisibilityConfig
                  @visibilityLevel="partner"
                  @partnerAccessConfig={{this.partnerAccessConfig}}
                  @availableCompanies={{or this.availableCompanies.value undefined}}
                  @onVisibilityLevelChange={{this.handleVisibilityLevelChange}}
                  @onPartnerAccessConfigChange={{this.handlePartnerAccessChange}}
                />
              {{/if}}
            {{/if}}
          </VStack>
        </Form>
      </:body>

      <:footer>
        <div class="tio-flex-end gap-x-3">
          <Button
            @appearance="outlined"
            {{on "click" this.handleCancel}}
            data-test-save-report-cancel
          >
            Cancel
          </Button>

          <Button
            type="submit"
            form="save-report-form"
            @intent="primary"
            @isRunning={{this.onSubmit.isRunning}}
            data-test-save-report-submit
          >
            {{#if this.onSubmit.isRunning}}
              Saving...
            {{else if (and this.hasCurrentReport (eq this.saveMode "update"))}}
              Update Report
            {{else}}
              Save New Report
            {{/if}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
